import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'sonner';
import { useTranslation } from './LanguageContext';

const BluetoothContext = createContext(null);

// Bitchat-style P2P mesh network configuration
const BLE_CONFIG = {
  SERVICE_UUID: '12345678-1234-5678-1234-56789abcdef0',
  CHARACTERISTIC_UUID: '12345678-1234-5678-1234-56789abcdef1',
  DEVICE_NAME_PREFIX: 'PAPO-',
  MAX_MESSAGE_LENGTH: 512,
  DISCOVERY_TIMEOUT: 30000,
  RECONNECT_INTERVAL: 5000
};

export const BluetoothProvider = ({ children }) => {
  const { t } = useTranslation();
  const [isSupported, setIsSupported] = useState(false);
  const [isEnabled, setIsEnabled] = useState(false);
  const [isScanning, setIsScanning] = useState(false);
  const [nearbyPeers, setNearbyPeers] = useState([]);
  const [connectedPeers, setConnectedPeers] = useState([]);
  const [meshMessages, setMeshMessages] = useState([]);
  const [myPeerId] = useState(() => `peer_${Date.now().toString(36)}_${Math.random().toString(36).substr(2, 9)}`);
  
  const deviceRef = useRef(null);
  const serverRef = useRef(null);
  const characteristicRef = useRef(null);
  // Refs for event handler callbacks to avoid stale closures in BLE listeners
  const handleIncomingMessageRef = useRef(null);
  const handlePeerDisconnectRef = useRef(null);

  // Check Bluetooth support
  useEffect(() => {
    const checkSupport = async () => {
      if ('bluetooth' in navigator) {
        try {
          const available = await navigator.bluetooth.getAvailability();
          setIsSupported(available);
        } catch (err) {
          console.error('Bluetooth availability check:', err);
          setIsSupported(false);
        }
      }
    };
    checkSupport();
  }, []);

  // Send message to a specific peer
  const sendToPeer = useCallback(async (peer, message) => {
    if (!peer.connected || !characteristicRef.current) return;

    const encoder = new TextEncoder();
    const data = encoder.encode(JSON.stringify(message));
    
    if (data.length > BLE_CONFIG.MAX_MESSAGE_LENGTH) {
      throw new Error('Message too long');
    }

    await characteristicRef.current.writeValue(data);
  }, []);

  // Handle peer disconnection
  const handlePeerDisconnect = useCallback((peerId) => {
    setNearbyPeers(prev => 
      prev.map(p => p.id === peerId ? { ...p, connected: false } : p)
    );
    setConnectedPeers(prev => prev.filter(p => p.id !== peerId));
    toast.info('Un peer se ha desconectado');
  }, []);

  // Relay message to other peers (mesh network behavior)
  const relayMessage = useCallback(async (message) => {
    // Don't relay our own messages or already relayed messages
    if (message.senderId === myPeerId) return;
    if (message.relayCount >= 5) return; // Max 5 hops

    const relayedMessage = {
      ...message,
      relayCount: (message.relayCount || 0) + 1,
      relayedBy: myPeerId
    };

    // Send to all connected peers
    for (const peer of connectedPeers) {
      try {
        await sendToPeer(peer, relayedMessage);
      } catch (err) {
        console.error('Send to peer error (may be disconnected):', err);
      }
    }
  }, [connectedPeers, myPeerId, sendToPeer]);

  // Handle incoming Bluetooth messages
  const handleIncomingMessage = useCallback((event) => {
    const value = event.target.value;
    const decoder = new TextDecoder();
    const messageStr = decoder.decode(value);
    
    try {
      const message = JSON.parse(messageStr);
      
      // Add message to mesh
      setMeshMessages(prev => {
        // Avoid duplicates
        if (prev.find(m => m.id === message.id)) return prev;
        return [...prev, {
          ...message,
          receivedAt: Date.now(),
          source: 'bluetooth'
        }].slice(-100); // Keep last 100 messages
      });

      // Relay message to other connected peers (mesh networking)
      relayMessage(message);
      
    } catch (error) {
      console.error('Error parsing Bluetooth message:', error);
    }
  }, [relayMessage]);

  // Keep refs in sync with latest callback versions
  useEffect(() => { handleIncomingMessageRef.current = handleIncomingMessage; }, [handleIncomingMessage]);
  useEffect(() => { handlePeerDisconnectRef.current = handlePeerDisconnect; }, [handlePeerDisconnect]);

  // Connect to a peer (uses refs for event handlers to avoid stale closures)
  const connectToPeer = useCallback(async (peer) => {
    try {
      const server = await peer.device.gatt.connect();
      serverRef.current = server;

      const service = await server.getPrimaryService(BLE_CONFIG.SERVICE_UUID);
      const characteristic = await service.getCharacteristic(BLE_CONFIG.CHARACTERISTIC_UUID);
      characteristicRef.current = characteristic;

      // Start notifications for incoming messages
      await characteristic.startNotifications();
      characteristic.addEventListener('characteristicvaluechanged', (e) => {
        handleIncomingMessageRef.current?.(e);
      });

      // Update peer status
      setNearbyPeers(prev => 
        prev.map(p => p.id === peer.id ? { ...p, connected: true } : p)
      );
      setConnectedPeers(prev => [...prev, { ...peer, connected: true }]);

      // Handle disconnection
      peer.device.addEventListener('gattserverdisconnected', () => {
        handlePeerDisconnectRef.current?.(peer.id);
      });

      toast.success(`Conectado a ${peer.name}`);
      return true;
    } catch (error) {
      console.error('Connection error:', error);
      toast.error(`Error al conectar con ${peer.name}`);
      return false;
    }
  }, []);

  // Start scanning for nearby PAPO peers
  const startScanning = useCallback(async () => {
    if (!isSupported) {
      toast.error('Bluetooth no está disponible en este dispositivo');
      return;
    }

    setIsScanning(true);
    
    try {
      // Request Bluetooth device with PAPO service
      const device = await navigator.bluetooth.requestDevice({
        filters: [
          { namePrefix: BLE_CONFIG.DEVICE_NAME_PREFIX },
          { services: [BLE_CONFIG.SERVICE_UUID] }
        ],
        optionalServices: [BLE_CONFIG.SERVICE_UUID]
      });

      deviceRef.current = device;
      
      // Add to nearby peers
      const peer = {
        id: device.id,
        name: device.name || t('bluetooth.papoUser'),
        device: device,
        connected: false,
        lastSeen: Date.now()
      };
      
      setNearbyPeers(prev => {
        const exists = prev.find(p => p.id === device.id);
        if (exists) return prev;
        return [...prev, peer];
      });

      // Connect to the device
      await connectToPeer(peer);
      
      toast.success(`Dispositivo encontrado: ${device.name}`);
    } catch (error) {
      if (error.name !== 'NotFoundError') {
        console.error('Bluetooth scan error:', error);
        toast.error('Error al buscar dispositivos');
      }
    } finally {
      setIsScanning(false);
    }
  }, [isSupported, connectToPeer]);

  // Send a message to the mesh network
  const sendMeshMessage = useCallback(async (content, type = 'chat') => {
    const message = {
      id: `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      senderId: myPeerId,
      content,
      type,
      timestamp: Date.now(),
      relayCount: 0
    };

    // Add to local messages
    setMeshMessages(prev => [...prev, { ...message, source: 'local' }]);

    // Send to all connected peers
    let sent = false;
    for (const peer of connectedPeers) {
      try {
        await sendToPeer(peer, message);
        sent = true;
      } catch (error) {
        console.error(`Failed to send to ${peer.name}:`, error);
      }
    }

    if (!sent && connectedPeers.length > 0) {
      toast.error('No se pudo enviar el mensaje');
    }

    return sent;
  }, [connectedPeers, myPeerId, sendToPeer]);

  // Enable/disable Bluetooth mode
  const toggleBluetooth = useCallback(async () => {
    if (isEnabled) {
      // Disconnect all peers
      for (const peer of connectedPeers) {
        try {
          await peer.device?.gatt?.disconnect();
        } catch (err) { console.error('Disconnect peer error:', err); }
      }
      setConnectedPeers([]);
      setNearbyPeers([]);
      setIsEnabled(false);
      toast.info('Modo Bluetooth desativado');
    } else {
      setIsEnabled(true);
      toast.success('Modo Bluetooth ativado - Pronto para conexoes P2P');
    }
  }, [isEnabled, connectedPeers]);

  // Panic mode - clear all data (like Bitchat)
  const panicMode = useCallback(() => {
    // Disconnect all
    for (const peer of connectedPeers) {
      try {
        peer.device?.gatt?.disconnect();
      } catch (err) { console.error('Panic disconnect error:', err); }
    }
    
    // Clear all data
    setConnectedPeers([]);
    setNearbyPeers([]);
    setMeshMessages([]);
    setIsEnabled(false);
    
    // Clear local storage
    localStorage.removeItem('papo_mesh_messages');
    
    toast.success('Todos los datos de mesh han sido eliminados');
  }, [connectedPeers]);

  const value = {
    // State
    isSupported,
    isEnabled,
    isScanning,
    nearbyPeers,
    connectedPeers,
    meshMessages,
    myPeerId,
    
    // Actions
    toggleBluetooth,
    startScanning,
    connectToPeer,
    sendMeshMessage,
    panicMode
  };

  return (
    <BluetoothContext.Provider value={value}>
      {children}
    </BluetoothContext.Provider>
  );
};

export const useBluetooth = () => {
  const context = useContext(BluetoothContext);
  if (!context) {
    throw new Error('useBluetooth must be used within a BluetoothProvider');
  }
  return context;
};
